import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import {
    DataGridPro,
    GridColDef,
    GridEventListener,
    GridRowScrollEndParams,
    GridSelectionModel,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import { Pagination } from 'src/types/common';
import LeadDataFormater from '../leads/view/LeadDataFormater';
import DateTimeDisplay from '../common/DateTimeDisplay';
import LocalFireDepartmentTwoTone from '@mui/icons-material/LocalFireDepartmentTwoTone';
import CheckIcon from '@mui/icons-material/Check';
import FallbackSpinner from '../../materio/@core/components/spinner';
import { LeadGridItem } from '../../types/Lead';
import { isEmpty } from 'lodash';
import {
    CadenceListDialogStore,
    DashboardViewStore,
    FilterSingleLead,
    Lead,
    LeadScreenStore,
} from '../../stores/LeadStore';
import Link from '@mui/material/Link';
import RenderIf, { ClaimType, ClaimValue } from '../common/RenderIf';
import { useQueryClient } from '@tanstack/react-query';
import { client } from '../../utils/axios-client';

interface CellType {
    row: LeadGridItem;
}

type Props = {
    selectedLeadIds: string[];
    leadPages: Pagination<LeadGridItem>[] | undefined;
    isLoading: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    onSelectionModeChange: (ids: GridSelectionModel) => void;
    preColumns: GridColDef<LeadGridItem>[];
    checkboxSelection?: boolean;
    postColumns?: GridColDef<LeadGridItem>[];
    allowLeadQuickView?: boolean;
    allowViewCadenceSteps?: boolean;
    onLeadClick: (lead: LeadGridItem) => void;
};

const LeadDataGridView = ({
    leadPages,
    selectedLeadIds,
    isLoading,
    hasNextPage,
    fetchNextPage,
    preColumns,
    onSelectionModeChange,
    onLeadClick,
    checkboxSelection = false,
    postColumns = [],
    allowLeadQuickView = false,
    allowViewCadenceSteps = false,
}: Props) => {
    const queryClient = useQueryClient();

    const viewCadenceSteps = async (lead: LeadGridItem) => {
        const leadData = await queryClient.fetchQuery<Lead>({
            queryKey: ['leads-list-loaded-lead', lead.id],
            queryFn: () => {
                return client.get(`/api/leads/get/${lead.id}`).then((res) => res.data);
            },
        });

        LeadScreenStore.update((s) => {
            s.selectedLead = leadData;
        });
        CadenceListDialogStore.update((s) => {
            s.cadenceListDialogMode = true;
        });
    };

    const viewQuickLead = async (lead: LeadGridItem) => {
        const leadData = await queryClient.fetchQuery<Lead>({
            queryKey: ['leads-list-loaded-lead', lead.id],
            queryFn: () => {
                return client.get(`/api/leads/get/${lead.id}`).then((res) => res.data);
            },
        });
        LeadScreenStore.update((s) => {
            s.selectedLead = leadData;
        });
        FilterSingleLead.update((s) => {
            s.filterSingleLead = true;
        });
        DashboardViewStore.update((s) => {
            s.showTodoList = true;
        });
        DashboardViewStore.update((s) => {
            s.showActivityWorkList = false;
        });
        DashboardViewStore.update((s) => {
            s.showInboundCallList = false;
        });
        DashboardViewStore.update((s) => {
            s.showDefault = false;
        });
        DashboardViewStore.update((s) => {
            s.showDetail = true;
        });
        DashboardViewStore.update((s) => {
            s.showTaskList = false;
        });
        //QuickLeadDialogStore.update(s => { s.quickLeadDialogMode = true })
    };

    const columns = [
        ...preColumns,
        {
            flex: 1,
            minWidth: 200,
            field: 'Lead',
            headerName: 'Lead',
            valueGetter: ({ row }: CellType) => row.fullName,
            hideable: false,
            renderCell: ({ row }: CellType) => {
                const { id, fullName } = row;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            {!allowLeadQuickView && (
                                <Typography noWrap variant="body2">
                                    {row.fullName}
                                </Typography>
                            )}

                            {allowLeadQuickView && (
                                <Link
                                    sx={{ color: 'text.primary', textDecoration: 'none' }}
                                    underline="hover"
                                    component="button"
                                    variant="caption"
                                    onClick={() => onLeadClick(row)}
                                >
                                    {row.fullName}
                                </Link>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 1,
            minWidth: 160,
            field: 'Company',
            headerName: 'Company',
            valueGetter: ({ row }: CellType) => row.company,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.company}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            minWidth: 160,
            field: 'Account',
            headerName: 'Account',
            valueGetter: ({ row }: CellType) => row.accountName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.accountName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            minWidth: 200,
            field: 'Activity',
            headerName: 'Activity',
            valueGetter: ({ row }: CellType) => row.nextActivityName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.nextActivityName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            minWidth: 150,
            field: 'Stage',
            headerName: 'Stage',
            valueGetter: ({ row }: CellType) => row.stageName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <LeadDataFormater
                                labelText=""
                                valueText={row.stageId != null ? row.stageName : ''}
                                textColor={row.stageId != null ? row.stageColor : ''}
                                errorText="-Stage is missing-"
                                chipHeight={20}
                                chipFontSize="0.875rem"
                                chipfontWeight={500}
                                isChip={true}
                            />
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 1.2,
            minWidth: 230,
            field: 'Cadence',
            headerName: 'Cadence',
            valueGetter: ({ row }: CellType) => row.nextActivityCadenceStepProcessOrder,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            {!allowViewCadenceSteps && (
                                <Typography noWrap variant="body2">
                                    {isEmpty(row.playbookCadenceName)
                                        ? ''
                                        : row.playbookCadenceName +
                                          ' (Step:' +
                                          row.nextActivityCadenceStepProcessOrder +
                                          ')'}
                                </Typography>
                            )}

                            {allowViewCadenceSteps && (
                                <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.ViewCadence}>
                                    <Link
                                        underline="hover"
                                        component="button"
                                        variant="caption"
                                        onClick={() => viewCadenceSteps(row)}
                                    >
                                        {isEmpty(row.playbookCadenceName)
                                            ? ''
                                            : row.playbookCadenceName +
                                              ' (Step:' +
                                              row.nextActivityCadenceStepProcessOrder +
                                              ')'}
                                    </Link>
                                </RenderIf>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            minWidth: 160,
            field: 'AccountRevenue',
            headerName: 'Account Revenue',
            valueGetter: ({ row }: CellType) => row.accountRevenueName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.accountRevenueName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },

        {
            flex: 0.9,
            minWidth: 150,
            field: 'AddressCity',
            headerName: 'City',
            valueGetter: ({ row }: CellType) => row.addressCity,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.addressCity}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 200,
            field: 'CountryName',
            headerName: 'Country',
            valueGetter: ({ row }: CellType) => row.countryName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.countryName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 170,
            field: 'DirectPhone',
            headerName: 'Direct Phone',
            valueGetter: ({ row }: CellType) => row.directPhone,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.directPhone}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 150,
            field: 'DncPhone',
            headerName: 'DNC Direct Phone',
            valueGetter: ({ row }: CellType) => row.dncPhone,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            {row.dncPhone ? <CheckIcon color="success" /> : ''}
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 150,
            field: 'DncMobile',
            headerName: 'DNC Mobile',
            valueGetter: ({ row }: CellType) => row.dncMobile,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            {row.dncMobile ? <CheckIcon color="success" /> : ''}
                        </Box>
                    </Box>
                );
            },
        },

        {
            flex: 0.9,
            minWidth: 150,
            field: 'DncSwitchboard',
            headerName: 'DNC Switchboard',
            valueGetter: ({ row }: CellType) => row.dncSwitchboard,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            {row.dncSwitchboard ? <CheckIcon color="success" /> : ''}
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 260,
            field: 'EmailAddress',
            headerName: 'Email',
            valueGetter: ({ row }: CellType) => row.emailAddress,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.emailAddress}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            minWidth: 160,
            field: 'EmployeeSize',
            headerName: 'Employee Size',
            valueGetter: ({ row }: CellType) => row.accountEmployeeSizeName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.accountEmployeeSizeName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 170,
            field: 'Fax',
            headerName: 'Fax',
            valueGetter: ({ row }: CellType) => row.fax,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.fax}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 170,
            field: 'LeadFunction',
            headerName: 'Function',
            valueGetter: ({ row }: CellType) => row.functionName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.functionName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 150,
            field: 'HotLead',
            headerName: 'Hot Lead',
            valueGetter: ({ row }: CellType) => row.hotLead,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            {row.hotLead ? (
                                <LocalFireDepartmentTwoTone color="error" />
                            ) : (
                                <LocalFireDepartmentTwoTone />
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 200,
            field: 'IndustryName',
            headerName: 'Industry',
            valueGetter: ({ row }: CellType) => row.industryName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.industryName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.2,
            minWidth: 230,
            field: 'JobTitle',
            headerName: 'Job Title',
            valueGetter: ({ row }: CellType) => row.jobTitle,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.jobTitle}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 200,
            field: 'LastActivityName',
            headerName: 'Last Activity',
            valueGetter: ({ row }: CellType) => row.lastActivityName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.lastActivityName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 160,
            field: 'LastActivityDateTime',
            headerName: 'Last Activity Date',
            valueGetter: ({ row }: CellType) => row.lastActivityDateTime,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                <DateTimeDisplay value={row.lastActivityDateTime} format={'date'} />
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 180,
            field: 'LastActivityOwner',
            headerName: 'Last Activity Owner',
            valueGetter: ({ row }: CellType) => row.lastActivityOwnerName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.lastActivityOwnerName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 160,
            field: 'LeadLevel',
            headerName: 'Level',
            valueGetter: ({ row }: CellType) => row.leadLevelName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.leadLevelName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 150,
            field: 'MeanigfulInteraction',
            headerName: 'MI',
            valueGetter: ({ row }: CellType) => row.meaningfulInteraction,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            {row.meaningfulInteraction ? <CheckIcon color="success" /> : ''}
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 170,
            field: 'MobilePhone',
            headerName: 'Mobile Phone',
            valueGetter: ({ row }: CellType) => row.mobilePhone,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.mobilePhone}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 190,
            field: 'NextActivityOwner',
            headerName: 'Next Activity Owner',
            valueGetter: ({ row }: CellType) => row.nextActivityOwnerName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.nextActivityOwnerName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 150,
            field: 'Owner',
            headerName: 'Owner',
            valueGetter: ({ row }: CellType) => row.ownerName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.ownerName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 140,
            field: 'AddressPostalCode',
            headerName: 'Postal Code',
            valueGetter: ({ row }: CellType) => row.addressPostalCode,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.addressPostalCode}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 160,
            field: 'AddressProvince',
            headerName: 'Province/State',
            valueGetter: ({ row }: CellType) => row.addressProvince,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.addressProvince}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 200,
            field: 'AddressRegion',
            headerName: 'Region',
            valueGetter: ({ row }: CellType) => row.addressRegionName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.addressRegionName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 100,
            field: 'Salutation',
            headerName: 'Salutation',
            valueGetter: ({ row }: CellType) => row.salutation,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.salutation}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 150,
            field: 'LeadSource',
            headerName: 'Source',
            valueGetter: ({ row }: CellType) => row.leadSourceName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.leadSourceName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 220,
            field: 'SprintName',
            headerName: 'Sprint',
            valueGetter: ({ row }: CellType) => row.sprintName,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.sprintName}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 240,
            field: 'AddressStreet',
            headerName: 'Street',
            valueGetter: ({ row }: CellType) => row.addressStreet,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.addressStreet}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 170,
            field: 'SwitchboardPhone',
            headerName: 'Switchboard',
            valueGetter: ({ row }: CellType) => row.switchboardPhone,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.switchboardPhone}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 260,
            field: 'SwitchboardEmail',
            headerName: 'Switchboard Email',
            valueGetter: ({ row }: CellType) => row.switchboardEmail,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.switchboardEmail}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            flex: 0.9,
            minWidth: 180,
            field: 'Website',
            headerName: 'Website',
            valueGetter: ({ row }: CellType) => row.website,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography noWrap variant="body2">
                                {row.website}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        ...postColumns,
    ];

    if (isLoading || leadPages === undefined)
        return (
            <Box sx={{ display: 'flex', height: '100%', flexGrow: 1, flex: 1, justifyContent: 'center' }}>
                <FallbackSpinner sx={{ height: undefined }} />
            </Box>
        );

    const pages = useMemo(() => {
        return leadPages.flatMap((page) => page.items);
    }, [leadPages]);

    const handleOnRowsScrollEnd = (params: GridRowScrollEndParams) => {
        if (hasNextPage) {
            fetchNextPage();
        }
    };

    return (
        <Card sx={{ width: '100%' }}>
            <div style={{ height: '70vh', width: '100%', overflow: 'auto' }}>
                <DataGridPro
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns
                                JobTitle: false,
                                EmailAddress: false,
                                CountryName: false,
                                IndustryName: false,
                                Salutation: false,
                                LeadLevel: false,
                                DirectPhone: false,
                                MobilePhone: false,
                                SwitchboardPhone: false,
                                LastActivityName: false,
                                LastActivityDateTime: false,
                                LastActivityOwner: false,
                                NextActivityName: false,
                                NextActivityDateTime: false,
                                NextActivityOwner: false,
                                AddressStreet: false,
                                AddressCity: false,
                                AddressProvince: false,
                                AddressPostalCode: false,
                                LeadFunction: false,
                                LeadSource: false,
                                AddressRegion: false,
                                Fax: false,
                                Website: false,
                                Owner: false,
                                SprintName: false,
                                AccountRevenue: false,
                                EmployeeSize: false,
                                MeanigfulInteraction: false,
                                SwitchboardEmail: false,
                                Edit: false,
                                Delete: false,
                                Override: false,
                                HotLead: false,
                                DncPhone: false,
                                DncMobile: false,
                                DncSwitchboard: false,
                            },
                        },
                    }}
                    rows={pages}
                    columns={columns}
                    onRowsScrollEnd={handleOnRowsScrollEnd}
                    disableSelectionOnClick
                    hideFooterPagination
                    checkboxSelection={checkboxSelection}
                    onSelectionModelChange={onSelectionModeChange}
                    experimentalFeatures={{ newEditingApi: true }}
                />
            </div>
        </Card>
    );
};

export { LeadDataGridView };
export default LeadDataGridView;
